import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { agentChatApiClient } from '@/services/api/agentChatService';
import type { TMaterializedCitation } from '@/services/types/agent-chat-types';
import { RootState } from '@/state/state';

export const useCitationDetails = (
  matterId: string,
  citationIds: string[],
): {
  citationMap: Record<string, TMaterializedCitation | undefined>;
  isLoading: boolean;
} => {
  const dispatch = useDispatch();

  // Prime the cache for all citation IDs on mount / when citationIds change
  useEffect(() => {
    citationIds.forEach((citationId) => {
      if (!citationId) return;
      dispatch(
        agentChatApiClient.util.prefetch(
          'getAgentChatCitation',
          { matterId, citationId },
          { force: false },
        ) as any,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, matterId, citationIds.join(',')]);

  // Select citation data from the store
  const selectCitationMap = useMemo(() => {
    return (state: RootState) => {
      const map: Record<string, TMaterializedCitation | undefined> = {};
      citationIds.forEach((citationId) => {
        if (!citationId) return;
        const selectCitation =
          agentChatApiClient.endpoints.getAgentChatCitation.select({
            matterId,
            citationId,
          });
        const citationState = selectCitation(state);
        map[citationId] = citationState.data;
      });
      return map;
    };
    // Use citationIds string to memoize when IDs truly change
  }, [citationIds, matterId]);

  const citationMap = useSelector(selectCitationMap, shallowEqual);

  const isLoading = useMemo(
    () => citationIds.some((id) => !citationMap[id]),
    [citationIds, citationMap],
  );

  return { citationMap, isLoading };
};
